/**
 * The one time anybody already using the portal is asked about notifications.
 *
 * WHY IT EXISTS: the invitation at the end of enrolment only ever fired
 * during enrolment, and every driver on the portal today enrolled before
 * notifications existed — so not one of them has ever been asked. The switch
 * in the menu has always been there, but nobody goes looking in a menu for
 * something they do not know they can have.
 *
 * WHAT IT IS CAREFUL ABOUT
 * ------------------------
 *   asked once      dismissing is remembered per device (deviceMemory), and
 *                   it does not come back. The menu — or, for the office,
 *                   the bell on the board — stays the permanent home, and
 *                   the card says so, so "not now" is not "never".
 *   undecided only  PushSetting shows a button only when asking would really
 *                   raise a prompt (`supportState` → READY). Already on, or
 *                   already refused, and this is silent: a browser that has
 *                   been told no cannot be asked again from a page, and a
 *                   button there would be a lie.
 *   a phone only    nobody at a desk is asked for notifications on a machine
 *                   they leave at six.
 *   one act at a time  on an iPhone that is not installed there IS no
 *                   prompt to raise, so this card stays silent and the Home
 *                   Screen offer — the honest next step — speaks alone.
 *
 * It renders `PushSetting` and nothing else: the permission prompt is raised
 * in exactly one place in this app, and this is not a second one.
 */
import React, { useState } from 'react';
import PushSetting from './PushSetting';
import { dismissOffer, isDismissed } from '../deviceMemory';
import { isHandheld } from '../installContext';

/** The keys each surface's dismissal is remembered under. */
export const PUSH_ASK_KEYS = Object.freeze({
  DRIVER: 'push-ask-driver',
  DISPATCHER: 'push-ask-dispatcher',
});

export default function PushAsk({ offerKey = PUSH_ASK_KEYS.DRIVER, copy, api }) {
  // Read once, on mount: a card that vanished mid-render because storage
  // changed under it would be a surprise, and the answer cannot change
  // without this component being the one that changed it.
  const [dismissed, setDismissed] = useState(() => isDismissed(offerKey));
  // The phone does not become a desk while somebody is looking at it.
  const [handheld] = useState(isHandheld);

  if (dismissed || !handheld) return null;

  const dismiss = () => {
    dismissOffer(offerKey);
    setDismissed(true);
  };

  return <PushSetting variant="ask" copy={copy} api={api} onDismiss={dismiss} />;
}
